/**
 * @file PhotoTypeEditDialog Organism Component
 * @description 사진 타입 편집 다이얼로그 컴포넌트
 * @see docs/plans/loading/05-implementation.md - Section 36
 */

import React, { useState, useCallback, useEffect, useMemo, memo, useRef } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Divider } from 'antd';
import Dialog from '@/src/components/atom/Dialog';
import BasicButton from '@/src/components/atom/Button/BasicButton';
import Icon from '@/src/components/atom/Icon';
import useAlert from '@/src/hooks/useAlert';
import PhotoTypeSlotList from '@/src/components/organism/PhotoTypeSlotList';
import colorSet from '@/src/styles/color';
import typo from '@/src/styles/typography';
import ResetSvg from '@/src/assets/icons/icon-filter-reset.svg';
import { ReactComponent as AddIcon } from '@/src/assets/icons/icon-add-blue.svg';
import type { PhotoTypeSlotData } from '@/src/components/molecule/PhotoTypeSlotItem';
import {
  useGetPhotoTypeConfigQuery,
  useUpdatePhotoTypeConfigMutation,
} from '@/src/store/apis/photoType';
import {
  CUSTOM_TYPE_PREFIX,
  PHOTO_TYPE_CONFIG,
  EDITABLE_PHOTO_TYPE_CODES,
  DEFAULT_PHOTO_TYPE_TITLES,
  type PhotoTypeCode,
  type DefaultPhotoTypeCode,
  type UpdatePhotoTypeConfigItem,
} from '@/src/store/apis/photoType/interface';
import {
  validateTitle,
  validateSubtitle,
  validateTypeCode,
} from '@/src/utils/photoType/validation';

export interface PhotoTypeEditDialogProps {
  /** 다이얼로그 열림 상태 */
  open: boolean;
  /** 다이얼로그 닫기 핸들러 */
  onClose: () => void;
  /** 저장 성공 핸들러 */
  onSuccess?: () => void;
}

const DialogContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: 720px;
  overflow: hidden;
  padding: 24px;
`;

const FixedHeader = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
`;

const ScrollableContent = styled.div`
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const FooterContainer = styled.div`
  display: flex;
  gap: 8px;
  width: 100%;
`;

const FooterButton = styled(BasicButton)`
  flex: 1;
`;

const DescriptionSection = styled.div`
  text-align: center;
  margin-bottom: 24px;
`;

const Description = styled.h2`
  ${typo.h2}
  color: ${colorSet.gray2};
  margin: 0 0 12px 0;
`;

const HelperText = styled.p`
  ${typo.b7m}
  color: ${colorSet.gray2};
  margin: 0;
  white-space: pre-line;
`;

/**
 * 리스트 헤더 컨테이너
 * Figma: gap-[10px] between left and right sections
 */
const ListHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
`;

/**
 * 활성 숫자 (파란색)
 * Figma: text-[#166dd7] text-[16px] (blue4)
 */
const ActiveCountNumber = styled.span`
  ${typo.b7m}
  color: ${colorSet.blue4};
`;

const ActiveCountTotal = styled.span`
  ${typo.b7m}
  color: ${colorSet.gray5};
`;

const ActiveLabel = styled.span`
  ${typo.b7m}
  color: ${colorSet.gray2};
`;

const ButtonContent = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const StyledAddIcon = styled(AddIcon)`
  width: 16px;
  height: 16px;
  path {
    fill: ${colorSet.indigo};
  }

  button:disabled & {
    path {
      fill: ${colorSet.gray7};
    }
  }
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  padding-right: 2px;
`;

const StyledDivider = styled(Divider)`
  margin: 0;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  height: 100%;
  color: ${colorSet.gray4};
  ${typo.b7r}
  white-space: pre-line;
`;

/**
 * API 응답을 슬롯 데이터로 변환
 */
const convertToSlotData = (
  configs: Array<{
    id: number;
    typeCode: PhotoTypeCode;
    title: string;
    subTitle: string | null;
    displayOrder: number;
    isActive: boolean;
  }>
): PhotoTypeSlotData[] => {
  return configs.map((config) => ({
    id: `slot-${config.id}`,
    typeCode: config.typeCode,
    title: config.title,
    subTitle: config.subTitle,
    displayOrder: config.displayOrder,
    isActive: config.isActive,
  }));
};

/**
 * 새 슬롯 생성
 */
const createNewSlot = (
  index: number,
  usedDefaultTypes: DefaultPhotoTypeCode[]
): PhotoTypeSlotData => {
  // 사용되지 않은 첫 번째 편집 가능한 타입 찾기 (CLOSE_UP_SEAL, EXTRA 제외)
  const availableDefaultType = EDITABLE_PHOTO_TYPE_CODES.find(
    (code) => !usedDefaultTypes.includes(code)
  );

  // 기본 타입이 모두 사용 중이면 커스텀 타입 사용
  const typeCode: PhotoTypeCode =
    availableDefaultType || (`CUSTOM_${index - 6}` as PhotoTypeCode);

  const title =
    availableDefaultType
      ? DEFAULT_PHOTO_TYPE_TITLES[availableDefaultType]
      : 'Custom';

  return {
    id: `new-slot-${Date.now()}-${index}`,
    typeCode,
    title,
    subTitle: null,
    displayOrder: index + 1,
    isActive: true,
  };
};

/**
 * PhotoTypeEditDialog - 사진 타입 편집 다이얼로그
 *
 * @description
 * 사진 타입 설정을 편집할 수 있는 다이얼로그입니다.
 * - 슬롯 추가/삭제 (7~15개)
 * - 드래그 앤 드롭으로 순서 변경
 * - 타입, 제목, 부제목 편집
 * - 활성화/비활성화 토글
 * - 초기화 기능
 *
 * @example
 * ```tsx
 * const [isOpen, setIsOpen] = useState(false);
 *
 * <PhotoTypeEditDialog
 *   open={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   onSuccess={() => {
 *     setIsOpen(false);
 *     refetchData();
 *   }}
 * />
 * ```
 */
const PhotoTypeEditDialogComponent: React.FC<PhotoTypeEditDialogProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation(['photoType', 'common']);
  const alert = useAlert();

  // API 훅
  const { data: configData, isLoading: isLoadingConfig } =
    useGetPhotoTypeConfigQuery(undefined, { skip: !open });
  const [updateConfig, { isLoading: isUpdating }] =
    useUpdatePhotoTypeConfigMutation();

  // 로컬 상태
  const [slots, setSlots] = useState<PhotoTypeSlotData[]>([]);
  const [errors, setErrors] = useState<
    Record<string, { titleError?: string | null; subtitleError?: string | null; typeCodeError?: string | null }>
  >({});
  const [autoFocusSlotId, setAutoFocusSlotId] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // API 데이터로 초기화
  useEffect(() => {
    if (configData?.photoTypeConfigs) {
      setSlots(convertToSlotData(configData.photoTypeConfigs));
      setErrors({});
      setAutoFocusSlotId(null);
    }
  }, [configData]);

  const usedDefaultTypes = useMemo(() => {
    return slots
      .filter((slot) => !slot.typeCode.startsWith(CUSTOM_TYPE_PREFIX))
      .map((slot) => slot.typeCode as DefaultPhotoTypeCode);
  }, [slots]);

  const canAdd = slots.length < PHOTO_TYPE_CONFIG.MAX_SLOTS;

  const hasChanges = useMemo(() => {
    if (!configData?.photoTypeConfigs) return false;

    const originalSlots = convertToSlotData(configData.photoTypeConfigs);

    if (slots.length !== originalSlots.length) return true;

    return slots.some((slot, index) => {
      const original = originalSlots[index];
      return (
        slot.typeCode !== original.typeCode ||
        slot.title !== original.title ||
        slot.subTitle !== original.subTitle ||
        slot.displayOrder !== original.displayOrder ||
        slot.isActive !== original.isActive
      );
    });
  }, [slots, configData]);

  const handleValidateSlot = useCallback(
    (index: number, field: 'typeCode' | 'title' | 'subTitle', newValue?: string) => {
      const slot = slots[index];
      if (!slot) return;

      if (field === 'typeCode') {
        const typeCodeCounts = slots.reduce((acc, s, i) => {
          const code = i === index && newValue !== undefined ? newValue : s.typeCode;
          if (!code.startsWith('CUSTOM')) {
            acc[code] = (acc[code] || 0) + 1;
          }
          return acc;
        }, {} as Record<string, number>);

        const duplicateTypeCodes = Object.keys(typeCodeCounts).filter(
          (code) => typeCodeCounts[code] > 1
        );

        setErrors((prev) => {
          const newErrors = { ...prev };

          slots.forEach((s, i) => {
            const slotTypeCode = i === index && newValue !== undefined ? newValue : s.typeCode;
            const current = newErrors[s.id] || {};
            const typeCodeError = duplicateTypeCodes.includes(slotTypeCode)
              ? 'photoType:validation.duplicateType'
              : null;

            const updated = {
              ...current,
              typeCodeError,
            };

            if (!updated.titleError && !updated.subtitleError && !updated.typeCodeError) {
              delete newErrors[s.id];
            } else {
              newErrors[s.id] = updated;
            }
          });

          return newErrors;
        });
        return;
      }

      let fieldError: string | null = null;

      if (field === 'title') {
        const valueToValidate = newValue !== undefined ? newValue : slot.title;
        fieldError = validateTitle(valueToValidate);
      } else if (field === 'subTitle') {
        const valueToValidate = newValue !== undefined ? newValue : slot.subTitle;
        fieldError = validateSubtitle(valueToValidate);
      }

      const slotId = slot.id;
      setErrors((prev) => {
        const current = prev[slotId] || {};
        const errorKey = field === 'title' ? 'titleError' : 'subtitleError';
        const updated = { ...current, [errorKey]: fieldError };

        if (!updated.titleError && !updated.subtitleError && !updated.typeCodeError) {
          const { [slotId]: _, ...rest } = prev;
          return rest;
        }

        return { ...prev, [slotId]: updated };
      });
    },
    [slots]
  );

  const validateSlots = useCallback((): boolean => {
    const newErrors: typeof errors = {};
    let isValid = true;

    const typeCodeCounts = slots.reduce((acc, slot) => {
      if (!slot.typeCode.startsWith('CUSTOM')) {
        acc[slot.typeCode] = (acc[slot.typeCode] || 0) + 1;
      }
      return acc;
    }, {} as Record<string, number>);
    const duplicateTypeCodes = Object.keys(typeCodeCounts).filter(
      (code) => typeCodeCounts[code] > 1
    );

    slots.forEach((slot) => {
      const titleError = validateTitle(slot.title);
      const subtitleError = validateSubtitle(slot.subTitle);
      let typeCodeError = validateTypeCode(slot.typeCode);

      if (!typeCodeError && duplicateTypeCodes.includes(slot.typeCode)) {
        typeCodeError = 'photoType:validation.duplicateType';
      }

      if (titleError || subtitleError || typeCodeError) {
        newErrors[slot.id] = { titleError, subtitleError, typeCodeError };
        isValid = false;
      }
    });

    const activeCount = slots.filter((s) => s.isActive).length;
    if (activeCount < PHOTO_TYPE_CONFIG.MIN_SLOTS) {
      alert.showAlert({
        type: 'error',
        message: t('photoType:validation.minActiveSlots'),
      });
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  }, [slots, t, alert]);

  /**
   * 슬롯 추가
   */
  const handleAddSlot = useCallback(() => {
    if (!canAdd) return;

    const newSlot = createNewSlot(slots.length, usedDefaultTypes);
    setSlots([...slots, newSlot]);
    setAutoFocusSlotId(newSlot.id);

    requestAnimationFrame(() => {
      if (listRef.current) {
        listRef.current.scrollTo({
          top: listRef.current.scrollHeight,
          behavior: 'smooth',
        });
      }
    });
  }, [slots, canAdd, usedDefaultTypes]);

  const handleReset = useCallback(() => {
    const defaultSlots: PhotoTypeSlotData[] = EDITABLE_PHOTO_TYPE_CODES.map((code, index) => ({
      id: `default-slot-${index}`,
      typeCode: code,
      title: DEFAULT_PHOTO_TYPE_TITLES[code],
      subTitle: null,
      displayOrder: index + 1,
      isActive: true,
    }));
    setSlots(defaultSlots);
    setErrors({});
    setAutoFocusSlotId(null);
  }, []);

  /**
   * 저장
   */
  const handleSave = useCallback(async () => {
    if (!validateSlots()) return;

    const payload: UpdatePhotoTypeConfigItem[] = slots.map((slot) => ({
      typeCode: slot.typeCode,
      title: slot.title,
      subTitle: slot.subTitle,
      displayOrder: slot.displayOrder,
      isActive: slot.isActive ?? true,
    }));

    try {
      await updateConfig({ photoTypeConfigs: payload }).unwrap();
      alert.showAlert({
        type: 'success',
        message: t('photoType:saveSuccess'),
      });
      onSuccess?.();
      onClose();
    } catch (error) {
      alert.showAlert({
        type: 'error',
        message: t('photoType:saveError'),
      });
    }
  }, [slots, validateSlots, updateConfig, t, onSuccess, onClose, alert]);

  /**
   * 취소
   */
  const handleCancel = useCallback(() => {
    if (configData?.photoTypeConfigs) {
      setSlots(convertToSlotData(configData.photoTypeConfigs));
    }
    setErrors({});
    setAutoFocusSlotId(null);
    onClose();
  }, [configData, onClose]);

  const isLoading = isLoadingConfig || isUpdating;

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => !isOpen && handleCancel()}
      title={t('photoType:editDialogTitle')}
      width={800}
      modalMaxHeight={900}
      responsiveMargin={40}
      isNotPadding
      footer={
        <FooterContainer>
          <FooterButton
            buttonGrade="tertiary"
            buttonColor="black"
            buttonSize={40}
            onClick={handleCancel}
            disabled={isLoading}
          >
            {t('common:cancel')}
          </FooterButton>
          <FooterButton
            buttonGrade="primary"
            buttonColor="blue"
            buttonSize={40}
            onClick={handleSave}
            disabled={isLoading || !hasChanges}
          >
            {t('photoType:submitButton')}
          </FooterButton>
        </FooterContainer>
      }
    >
      <DialogContent>
        <FixedHeader>
          <DescriptionSection>
            <Description>{t('photoType:description')}</Description>
            <HelperText>{t('photoType:helperText')}</HelperText>
          </DescriptionSection>
          <ListHeader>
            <div>
              <ActiveLabel>{t('photoType:activeLabel')} </ActiveLabel>
              <ActiveCountNumber>
                {slots.filter(s => s.isActive).length}
              </ActiveCountNumber>
              <ActiveCountTotal>/{PHOTO_TYPE_CONFIG.MAX_SLOTS}</ActiveCountTotal>
            </div>
            <HeaderActions>
              <BasicButton
                buttonGrade="tertiary"
                buttonColor="black"
                buttonSize={32}
                onClick={handleReset}
                disabled={isLoading}
              >
                <ButtonContent>
                  <Icon iconSrc={ResetSvg} iconSize={16} />
                  {t('common:reset')}
                </ButtonContent>
              </BasicButton>
              <BasicButton
                buttonGrade="secondary"
                buttonColor="blue"
                buttonSize={32}
                onClick={handleAddSlot}
                disabled={!canAdd || isLoading}
              >
                <ButtonContent>
                  <StyledAddIcon />
                  {t('common:add')}
                </ButtonContent>
              </BasicButton>
            </HeaderActions>
          </ListHeader>
          <StyledDivider />
        </FixedHeader>
        <ScrollableContent ref={listRef}>
          {slots.length === 0 ? (
            <EmptyState>{t('photoType:noSlots')}</EmptyState>
          ) : (
            <PhotoTypeSlotList
              slots={slots}
              onSlotsChange={setSlots}
              onValidate={handleValidateSlot}
              errors={errors}
              disabled={isLoading}
              autoFocusSlotId={autoFocusSlotId}
            />
          )}
        </ScrollableContent>
      </DialogContent>
    </Dialog>
  );
};

PhotoTypeEditDialogComponent.displayName = 'PhotoTypeEditDialog';

export const PhotoTypeEditDialog = memo(PhotoTypeEditDialogComponent);
export default PhotoTypeEditDialog;
